import React, { useContext, useState, useEffect } from 'react';
import SectionTitle from "./SectionTitle";
import MultiLingualContent, { getText } from "../config/multilingualContent";
import { LanguageContext } from "../config/multilingualContext";
import { show as showCountriesOverride } from '../constants/countries.override';
import { 
    readDetB, readSimpB, readCombfgazm, readElecq, 
    readEnrbiomasse, readGaznatural, readPetrole, 
    readLastDetB, getCountries 
} from '../functions';
import '../assets/css/table.css';
import Detbalance from "./Detbalance";
import Simpbalance from "./Simpbalance";
import Combfgazmstat from "./Combfgazmstat";
import Elecqstat from "./Elecqstat";
import Enrbiomassestat from "./Enrbiomassestat";
import Gaznaturalstat from "./Gaznaturalstat";
import Petrolestat from "./Petrolestat";
import Loading from "./Loading";
import { SelectPicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';

const BalanceContent = () => {
    const { language } = useContext(LanguageContext);

    const [year, setYear] = useState("");
    const [balance, setBalance] = useState("");
    const [showdata, setShowdata] = useState("");
    const [loading, setLoading] = useState(false);

    const [configsColumn, setConfigsColumn] = useState([]);
    const [configsLine, setConfigsLine] = useState([]);
    const [configsUnit, setConfigsUnit] = useState({});
    const [tableStatData, setTableStatData] = useState([]);
    const [allUnits, setAllUnits] = useState(undefined);

    const [country, setCountry] = useState("");
    const [countriesForPicker, setCountriesForPicker] = useState([]);

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => {
        const y = (1900 + i).toString();
        return { label: y, value: y };
    }).reverse();

    const GET_BALANCE = (year, balance, country) => {
        switch (balance) {
            case 'desagrege': return readDetB(year, country);
            case 'agrege': return readSimpB(year, country);
            case 'combfgazm': return readCombfgazm(year, country);
            case 'elecq': return readElecq(year, country);
            case 'enrbiomasse': return readEnrbiomasse(year, country);
            case 'gaznatural': return readGaznatural(year, country);
            case 'petrole': return readPetrole(year, country);
            default: return null;
        }
    };

    // Load last detailed balance on mount
    useEffect(() => {
        let mounted = true;

        const loadLastDet = async () => {
            setLoading(true);
            try {
                const response = await readLastDetB();
                const { data } = await response;
                const countries = await getCountries();

                if (!mounted) return;

                const countryArray = countries?.map(item => ({
                    label: {
                        french: item.label_fr ?? "",
                        english: item.label_en ?? "",
                        portuguais: item.label_pr ?? "",
                        swahili: item.label_sw ?? "",
                    }[language],
                    value: String(item.id),
                    role: {
                        french: item.label_fr ?? "",
                        english: item.label_en ?? "",
                        portugais: item.label_pr ?? "",
                        swahili: item.label_sw ?? "",
                    }[language],
                }))?.sort((a, b) =>
                    (a.label || "").localeCompare(
                        b.label || "",
                        language === 'french' ? 'fr' : language === 'english' ? 'en' : language === 'portuguais' ? 'pt' : 'sw',
                        { sensitivity: 'base' }
                    )
                );
                setCountriesForPicker(countryArray);

                if (data) {
                    const { configsColumnAPI, configsLineAPI, tableStatDataAPI, configsUnit, year, country, units } = data;

                    setConfigsColumn(configsColumnAPI);
                    setConfigsLine(configsLineAPI);
                    setTableStatData(tableStatDataAPI);
                    setConfigsUnit(configsUnit);
                    setAllUnits(units);

                    setCountry(String(country ?? countries[0]?.id));
                    setBalance('desagrege');
                    setShowdata('desagrege');
                    setYear(String(year ?? ""));
                }
            } catch (err) {
                console.error('readLastDetB failed', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadLastDet();
        return () => { mounted = false };
    }, [language]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setShowdata(balance);

        try {
            const response = await GET_BALANCE(year, balance, country);
            const { data } = response || {};
            if (!data) return;

            const { configsColumnAPI, configsLineAPI, tableStatDataAPI, configsUnit, units } = data;

            if (balance === 'desagrege' || balance === 'agrege') {
                setAllUnits(units);
            }

            setConfigsColumn(configsColumnAPI);
            setConfigsLine(configsLineAPI);
            setTableStatData(tableStatDataAPI);
            setConfigsUnit(configsUnit);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    function SubmitButton() {
        return year && balance
            ? <button className="btn btn-xs btn-primary w-100 w-md-auto" type="submit"><MultiLingualContent contentID="validation" /></button>
            : <button disabled className="btn btn-xs btn-primary w-100 w-md-auto" type="submit"><MultiLingualContent contentID="validation" /></button>;
    }

    return (
        <div className="container bilan-list-items mb-5">
            <SectionTitle pageTitle="balance" />
            <form onSubmit={handleSubmit}>
                <div className="row mt-2">
                    <div className="col-lg-4 col-xl-4 col-md-4 col-xs-12 mt-3">
                        <SelectPicker
                            label={<MultiLingualContent contentID="sourcenbalance" />}
                            style={{ width: 500 }}
                            data={[
                                { label: <MultiLingualContent contentID="simplbalancedata" />, value: "agrege" },
                                { label: <MultiLingualContent contentID="detbalancedata" />, value: "desagrege" },
                                { label: <MultiLingualContent contentID="combfgazmdata" />, value: "combfgazm" },
                                { label: <MultiLingualContent contentID="elecqdata" />, value: "elecq" },
                                { label: <MultiLingualContent contentID="enrbiomassedata" />, value: "enrbiomasse" },
                                { label: <MultiLingualContent contentID="gaznaturaldata" />, value: "gaznatural" },
                                { label: <MultiLingualContent contentID="petroledata" />, value: "petrole" }
                            ]}
                            value={balance}
                            onChange={setBalance}
                            searchable={false}
                        />
                    </div>
                    <div className="col-lg-3 col-xl-3 col-md-3 col-xs-12 mt-3">
                        <SelectPicker
                            label={<MultiLingualContent contentID="year" />}
                            style={{ width: 500 }}
                            data={years}
                            value={year}
                            onChange={setYear}
                            cleanable
                        />
                    </div>
                    {!showCountriesOverride && (
                        <input type="hidden" name="country" value={country ?? ''} />
                    )}
                    
                    {showCountriesOverride && (
                        <div className="col-lg-3 col-xl-3 col-md-3 col-xs-12 mt-3">
                            <SelectPicker
                                label={<MultiLingualContent contentID="country" />}
                                data={countriesForPicker}
                                style={{ width: 500 }}
                                value={country}
                                onChange={setCountry}
                            />
                        </div>
                    )}
                    <div className="col-lg-2 col-xl-2 col-md-2 col-xs-12 mt-3 d-flex align-items-end">
                        <SubmitButton />
                    </div>
                </div>
            </form>

            <div className="row">
                <div className="bilan-list-items-bottom col-lg-12 col-xl-12 col-md-12 col-xs-12">
                    <div className="card mt-3">
                        <div className="card-body">
                            {loading ? <Loading /> :
                                <div className="bs-stepper wizard-numbered mt-2">
                                    <div style={{ minHeight: '100vh' }}>
                                        {{
                                            'agrege': tableStatData && <Simpbalance configsColumn={configsColumn} configsLine={configsLine} tableStatData={tableStatData} configsUnit={configsUnit} allUnits={allUnits} />,
                                            'desagrege': tableStatData && <Detbalance configsColumn={configsColumn} configsLine={configsLine} tableStatData={tableStatData} configsUnit={configsUnit} allUnits={allUnits} />,
                                            'combfgazm': tableStatData && <Combfgazmstat configsColumn={configsColumn} configsLine={configsLine} tableStatData={tableStatData} />,
                                            'elecq': tableStatData && <Elecqstat configsColumn={configsColumn} configsLine={configsLine} tableStatData={tableStatData} />,
                                            'enrbiomasse': tableStatData && <Enrbiomassestat configsColumn={configsColumn} configsLine={configsLine} tableStatData={tableStatData} />,
                                            'gaznatural': tableStatData && <Gaznaturalstat configsColumn={configsColumn} configsLine={configsLine} tableStatData={tableStatData} />,
                                            'petrole': tableStatData && <Petrolestat configsColumn={configsColumn} configsLine={configsLine} tableStatData={tableStatData} />,
                                        }[showdata]}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BalanceContent;